import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions, // Import DialogActions
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
  List, // Import List
  ListItem, // Import ListItem
  ListItemText, // Import ListItemText
  IconButton, // Import IconButton
  Snackbar, // Import Snackbar for success message
  Alert // Import Alert for Snackbar content
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete'; // Import delete icon
import config from '../../js/config';
import { getBearerString } from '../../js/jwt_token';

// Define the interface for the component's props
interface GenericFolderUploadProps {
  data_type: string;
  buttonText?: string; // Optional prop for the upload button text
  onUploadSuccess?: () => void; // Callback for successful upload
  onUploadError?: (error: string) => void; // Callback for upload error
}

const GenericFolderUpload: React.FC<GenericFolderUploadProps> = ({
  data_type,
  buttonText = 'Upload Files',
  onUploadSuccess,
  onUploadError,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploadInProgressDialog, setIsUploadInProgressDialog] = useState<boolean>(false);
  const [isFileReviewDialogOpen, setIsFileReviewDialogOpen] = useState<boolean>(false); // New state for file review dialog
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState<boolean>(false); // State for success popup

  const inputRef = useRef<HTMLInputElement>(null);

  /**************************
  Handle React State Changes
  **************************/
  const handleFolderSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles) {
      const fileArray = Array.from(selectedFiles);
      setFiles(fileArray);
      setErrorMessage(null); // Clear any previous error message
      if (fileArray.length > 0) {
        setIsFileReviewDialogOpen(true); // Open the file review dialog
      }
    }
    // Clear the input's value to allow selecting the same folder again
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleRemoveFile = (indexToRemove: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, index) => index !== indexToRemove));
  };

  const handleCloseFileReviewDialog = () => {
    setIsFileReviewDialogOpen(false);
    setFiles([]); // Clear selected files if dialog is closed without confirming
  };

  const uploadFolder = () => {
    if (files.length === 0) {
      setErrorMessage('No files selected for upload. Please select a folder first.');
      return;
    }

    setIsFileReviewDialogOpen(false); // Close the file review dialog
    setIsUploadInProgressDialog(true); // Open the upload progress dialog

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('data_type', data_type);

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded * 100) / event.total);
        setUploadProgress(progress);
      }
    });

    xhr.open('POST', config.data_platform_api_url + "/data/upload", true);

    xhr.setRequestHeader('Authorization', getBearerString());

    xhr.onload = () => {
      if (xhr.status === 200) {
        console.log('Files uploaded successfully');
        setFiles([]);
        setUploadProgress(0);
        setErrorMessage(null);
        setShowSuccessSnackbar(true); // Show success snackbar
        if (onUploadSuccess) {
          onUploadSuccess(); // Call success callback
        }
      } else {
        console.error('Failed to upload files', xhr.responseText);
        const errorMsg = `Upload failed: ${xhr.statusText || 'Unknown error'}. Please try again.`;
        setErrorMessage(errorMsg);
        if (onUploadError) {
          onUploadError(errorMsg); // Call error callback
        }
      }
      setIsUploadInProgressDialog(false); // Close upload progress dialog
    };

    xhr.onerror = () => {
      console.error('Error uploading files');
      const errorMsg = 'Network error or server unreachable. Please check your connection.';
      setErrorMessage(errorMsg);
      if (onUploadError) {
        onUploadError(errorMsg); // Call error callback
      }
      setIsUploadInProgressDialog(false); // Close upload progress dialog
    };

    setUploadProgress(0); // Reset progress when starting a new upload
    xhr.send(formData);
  };

  useEffect(() => {
    if (inputRef.current !== null) {
      inputRef.current.setAttribute("directory", "true");
      inputRef.current.setAttribute("webkitdirectory", "");
    }
  }, [inputRef]);

  const handleCloseSuccessSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowSuccessSnackbar(false);
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        multiple
        onChange={handleFolderSelect}
        style={{ display: 'none' }}
        id="folder-upload-input"
      />
      <label htmlFor="folder-upload-input">
        <Button
          component="span"
          variant="text"
        >
          {buttonText}
        </Button>
      </label>

      {errorMessage && (
        <Typography color="error" style={{ marginTop: '10px' }}>
          {errorMessage}
        </Typography>
      )}

      {/* File Review Dialog */}
      <Dialog open={isFileReviewDialogOpen} onClose={handleCloseFileReviewDialog} fullWidth maxWidth="sm">
        <DialogTitle>Review Files for Upload ({files.length} files)</DialogTitle>
        <DialogContent dividers>
          {files.length === 0 ? (
            <Typography variant="body1" color="textSecondary">No files selected. Please select a folder to upload.</Typography>
          ) : (
            <List dense>
              {files.map((file, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveFile(index)}>
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={file.name}
                    secondary={`${(file.size / 1024).toFixed(2)} KB`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileReviewDialog}>Cancel</Button>
          <Button
            onClick={uploadFolder}
            color="primary"
            variant="contained"
            disabled={files.length === 0}
          >
            Confirm Upload ({files.length} files)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload In Progress Dialog */}
      <Dialog open={isUploadInProgressDialog} onClose={() => { /* Prevent closing during upload */ }} disableEscapeKeyDown>
        <DialogTitle>Uploading Files</DialogTitle>
        <DialogContent>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>{`${uploadProgress}%`}</Typography>
        </DialogContent>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSuccessSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSuccessSnackbar} severity="success" sx={{ width: '100%' }}>
          Files uploaded successfully!
        </Alert>
      </Snackbar>
    </div>
  );
};

export default GenericFolderUpload;